import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { MfaSetup } from "@/components/mfa/MfaSetup";

// Shown after /auth/login returns mfa_required. Handles both the normal TOTP
// challenge and the mandatory pre-login enrollment (enrollmentRequired).
export const MfaChallengeStep = ({ challengeToken, enrollmentRequired, onSuccess }) => {
  const { mfaChallenge, mfaRecovery } = useAuth();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (enrollmentRequired) {
    return (
      <div data-testid="mfa-enrollment-required">
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Your role requires two-factor authentication. Set it up now to finish signing in.
        </div>
        <MfaSetup mode="challenge" challengeToken={challengeToken} onComplete={(res) => onSuccess?.(res?.user)} />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (useRecovery) await mfaRecovery(challengeToken, code.trim());
      else await mfaChallenge(challengeToken, code.trim());
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="mfa-challenge">
      <div className="flex items-center gap-2 text-slate-900">
        <ShieldCheck className="h-5 w-5 text-emerald-600" />
        <h3 className="font-display text-lg font-bold">Two-factor verification</h3>
      </div>
      <p className="text-sm text-slate-600">
        {useRecovery ? "Enter one of your saved recovery codes." : "Enter the 6-digit code from your authenticator app."}
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-slate-400" />
          {useRecovery ? (
            <Input data-testid="mfa-recovery-input" placeholder="XXXX-XXXX-XXXX" value={code} onChange={(e) => setCode(e.target.value)} className="font-mono-arps tracking-widest" />
          ) : (
            <Input data-testid="mfa-code-input" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="font-mono-arps tracking-widest" />
          )}
        </div>
        <Button data-testid="mfa-verify-button" type="submit" disabled={busy || (!useRecovery && code.length !== 6) || (useRecovery && code.length < 8)} className="w-full bg-slate-900 text-white hover:bg-slate-800">
          {busy ? "Verifying…" : "Verify & sign in"}
        </Button>
      </form>
      <button data-testid="mfa-toggle-recovery" type="button" onClick={() => { setUseRecovery((v) => !v); setCode(""); }} className="text-sm font-medium text-emerald-600 hover:underline">
        {useRecovery ? "Use authenticator code instead" : "Use a recovery code"}
      </button>
    </div>
  );
};
